import FinancedSubjectType from "@/module/main/dto/FinancedSubjectTypeEnum";
import Brand from "@/module/main/model/applicationForm/BrandEnum";
import IEnvCredential from "@/module/main/model/applicationForm/IEnvCredential";
import EnvCredential from "@/module/main/model/applicationForm/EnvCredential";
import TemplateOptions from "@/module/main/page/TemplateOptionsEnum";
import OrderType from "@/module/main/dto/OrderTypeEnum";
import CarLoanType from "@/module/main/dto/CarLoanTypeEnum";

class BrandConfig {
    constructor(public readonly brand: Brand,
                public readonly financedSubjectTypes: FinancedSubjectType[],
                public readonly orderType?: OrderType,
                public readonly carLoanType?: CarLoanType) {
    }
}

export default class Brands {

    static readonly HOMECREDIT = new BrandConfig(Brand.HOMECREDIT, [FinancedSubjectType.CASH, FinancedSubjectType.LIABILITY]);
    static readonly HC_MAPP = new BrandConfig(Brand.HC_MAPP, [FinancedSubjectType.CASH]);
    static readonly TESCO = new BrandConfig(Brand.TESCO, [FinancedSubjectType.CASH, FinancedSubjectType.LIABILITY]);
    static readonly MALL = new BrandConfig(Brand.MALL, [FinancedSubjectType.ORDER], OrderType.STANDARD);
    static readonly CZC = new BrandConfig(Brand.CZC, [FinancedSubjectType.ORDER], OrderType.STANDARD);
    static readonly ALZA = new BrandConfig(Brand.ALZA, [FinancedSubjectType.ORDER], OrderType.STANDARD);
    static readonly TESTSHOP = new BrandConfig(Brand.TESTSHOP, [FinancedSubjectType.ORDER], OrderType.STANDARD);
    static readonly COMGATE = new BrandConfig(Brand.COMGATE, [FinancedSubjectType.ORDER], OrderType.BROKERED);
    static readonly HC_OL = new BrandConfig(Brand.HC_OL, [FinancedSubjectType.ORDER], OrderType.OPERATING_LEASE);
    static readonly EKIS = new BrandConfig(Brand.EKIS, [FinancedSubjectType.ORDER], OrderType.CAR_LOAN, CarLoanType.SMALL_CARS);
    static readonly AUTO_LOUDA = new BrandConfig(Brand.AUTO_LOUDA, [FinancedSubjectType.ORDER], OrderType.CAR_LOAN, CarLoanType.LARGE_CARS);
    static readonly ALZA_PAY = new BrandConfig(Brand.ALZA, [FinancedSubjectType.WALLET]);

    private static values: BrandConfig[] = [Brands.HOMECREDIT, Brands.TESCO, Brands.TESTSHOP, Brands.MALL, Brands.CZC,
        Brands.ALZA, Brands.COMGATE, Brands.HC_OL, Brands.HC_MAPP, Brands.EKIS, Brands.AUTO_LOUDA, Brands.ALZA_PAY];

    public static getBrands(financedSubjectType: FinancedSubjectType, orderType?: OrderType, carLoanType?: CarLoanType): Brand[]
    {
        let brands = this.values.filter(x => x.financedSubjectTypes.includes(financedSubjectType));

        if (financedSubjectType === FinancedSubjectType.ORDER && !!orderType)
            brands = brands.filter(x => x.orderType === orderType);

        if (financedSubjectType === FinancedSubjectType.ORDER && orderType === OrderType.CAR_LOAN && !!carLoanType)
            brands = brands.filter(x => x.carLoanType === carLoanType);

        return brands.map(x => x.brand);
    }

    public static getBrand(financedSubjectType: FinancedSubjectType, orderType: OrderType, carLoanType?: CarLoanType): Brand
    {
        return this.getBrands(financedSubjectType, orderType, carLoanType)[0];
    }

    public static getCredential(brand: Brand, template: TemplateOptions): IEnvCredential | undefined
    {
        return this.getCredentials().find(credential => credential.brand === brand && credential.template === template);
    }

    public static getCredentials(): IEnvCredential[] {
        return [
            new EnvCredential(Brand.HOMECREDIT, TemplateOptions.CZ, "loki", "loki"),
            new EnvCredential(Brand.HOMECREDIT, TemplateOptions.SK, "loki", "loki"),
            new EnvCredential(Brand.TESCO, TemplateOptions.CZ, "TescoONL025238", "TescoONL025238"),
            new EnvCredential(Brand.TESCO, TemplateOptions.SK, "TescoONL013434", "TescoONL013434"),
            new EnvCredential(Brand.TESTSHOP, TemplateOptions.CZ, "testshop", "testshop"),
            new EnvCredential(Brand.TESTSHOP, TemplateOptions.SK, "testshop", "testshop"),
            new EnvCredential(Brand.MALL, TemplateOptions.CZ, "mall", "mall"),
            new EnvCredential(Brand.MALL, TemplateOptions.SK, "mall", "mall"),
            new EnvCredential(Brand.CZC, TemplateOptions.CZ, "czc", "czc"),
            new EnvCredential(Brand.CZC, TemplateOptions.SK, "czc", "czc"),
            new EnvCredential(Brand.ALZA, TemplateOptions.CZ, "026500tech", "026500tech"),
            new EnvCredential(Brand.ALZA, TemplateOptions.SK, "014904tech", "014904tech"),
            new EnvCredential(Brand.COMGATE, TemplateOptions.CZ, "028393tech", "028393tech"),
            new EnvCredential(Brand.COMGATE, TemplateOptions.SK, "testshop", "testshop"),
            new EnvCredential(Brand.HC_OL, TemplateOptions.CZ, "operak", "operak"),
            new EnvCredential(Brand.HC_OL, TemplateOptions.SK, "operak", "operak"),
            new EnvCredential(Brand.HC_MAPP, TemplateOptions.CZ, "mapp-flexi", "mapp-flexi"),
            new EnvCredential(Brand.HC_MAPP, TemplateOptions.SK, "mapp-flexi", "mapp-flexi"),
            new EnvCredential(Brand.EKIS, TemplateOptions.CZ, "lokiApp", "lokiApp"),
            new EnvCredential(Brand.EKIS, TemplateOptions.SK, "lokiApp", "lokiApp"),
            new EnvCredential(Brand.AUTO_LOUDA, TemplateOptions.CZ, "032471tech", "032471tech"),
        ];
    }
}
